import { type SubmissionQuotasQuery } from "../api/queries/submissionQuotas.gql-generated";
import {
    type SubmissionQuota,
    SubmissionQuotaContext,
    SubmissionQuotaSeverity,
    type SubmissionQuotas,
} from "../models/SubmissionQuota";

type SubmissionQuotasData = SubmissionQuotasQuery["submissionQuotas"];
type SubmissionQuotaSummaryData = NonNullable<NonNullable<SubmissionQuotasData>["regular"]>;

type SubmissionQuotaSeverityInput = Pick<SubmissionQuotaSummaryData, "remaining" | "remainingPercentage">;

export const getSubmissionQuotaSeverity = (
    quota: SubmissionQuotaSeverityInput | null,
): SubmissionQuotaSeverity | null => {
    if (!quota) {
        return null;
    }
    if (quota.remaining <= 0) {
        return SubmissionQuotaSeverity.Exhausted;
    }
    if (quota.remainingPercentage <= 10) {
        return SubmissionQuotaSeverity.Urgent;
    }
    if (quota.remainingPercentage <= 33) {
        return SubmissionQuotaSeverity.Warning;
    }
    return SubmissionQuotaSeverity.Normal;
};

const normalizeReplenishAt = (replenishAt: string | null | undefined): Date | null => {
    if (!replenishAt) {
        return null;
    }
    const date = new Date(replenishAt);
    return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeSubmissionQuota = (quota: SubmissionQuotaSummaryData | null | undefined): SubmissionQuota | null => {
    if (!quota) {
        return null;
    }
    const remaining = Math.max(quota.remaining, 0);
    const remainingPercentage = Math.min(Math.max(quota.remainingPercentage, 0), 100);
    const severity = getSubmissionQuotaSeverity({ remaining, remainingPercentage });

    return {
        remaining,
        remainingPercentage,
        limitingQuotaType: quota.limitingQuotaType,
        limitingWindow: quota.limitingWindow,
        replenishAt: normalizeReplenishAt(quota.replenishAt),
        severity: severity ?? SubmissionQuotaSeverity.Normal,
    };
};

export const normalizeSubmissionQuotas = (quotas: SubmissionQuotasData | null | undefined): SubmissionQuotas => ({
    regular: normalizeSubmissionQuota(quotas?.regular ?? null),
    lensPlus: normalizeSubmissionQuota(quotas?.lensPlus ?? null),
});

export const getFreshSubmissionQuota = (
    quota: SubmissionQuota | null | undefined,
    now = Date.now(),
): SubmissionQuota | null => {
    if (!quota || (quota.replenishAt && quota.replenishAt.getTime() <= now)) {
        return null;
    }

    return quota;
};

export const isSubmissionQuotaExhaustedForEnforcement = (
    quota: SubmissionQuota | null | undefined,
    now = Date.now(),
): boolean => (getFreshSubmissionQuota(quota, now)?.remaining ?? 1) <= 0;

const upsellSeverities: ReadonlySet<SubmissionQuotaSeverity> = new Set([
    SubmissionQuotaSeverity.Urgent,
    SubmissionQuotaSeverity.Exhausted,
]);

/** Only the Lens+ pool has a paid tier above it, so only that pool is worth upselling. */
export const shouldOfferLensPlusUpsell = (quota: SubmissionQuota, context: SubmissionQuotaContext): boolean =>
    context === SubmissionQuotaContext.LensPlus && upsellSeverities.has(quota.severity);
